import React from 'react';
import { Text, Document, Page, View, StyleSheet, Image } from '@react-pdf/renderer';
import logoPdf from '../../../assets/img/logo-pdf.png';
import { AggregatedAlertsData, AggregatedReadsData } from '../Charts.types';

const styles = StyleSheet.create({
  page: {
    padding: '18pt',
    backgroundColor: '#F9FAFB',
  },
  detailedReportText: {
    fontSize: '12pt',
    color: '#101828',
    fontWeight: 600,
  },
  headerText: {
    fontWeight: 600,
    paddingBottom: '7pt',
    fontSize: '11pt',
    color: '#101828',
  },
  graph: {
    paddingBottom: '11pt',
    width: '100%',
  },
  header: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  logo: {
    height: '23pt',
    width: '23pt',
  },
  rangeText: {
    fontSize: '7pt',
    color: '#667085',
  },
  headerWrapper: {
    paddingBottom: '18pt',
  },
  totalWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '12pt',
    backgroundColor: '#FFFFFF',
    borderRadius: '6pt',
    width: '100%',
    marginBottom: '18pt',
  },
  totalReads: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '0pt',
    paddingBottom: '6pt',
  },
  totalReadsText: {
    fontSize: '10pt',
    lineHeight: '125%',
    color: '#667085',
    paddingBottom: '1pt',
  },
  totalReadsNumber: {
    fontWeight: 700,
    fontSize: '10pt',
    lineHeight: '125%',
    color: 'black',
  },
  eachKeySeparateWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: '0pt',
    width: '100%',
  },
  eachKeySeparate: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: '0pt',
    flexGrow: 1,
  },
  tableWrapper: {
    borderRadius: '6pt',
    width: '100%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #EAECF0',
    marginBottom: '18pt',
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: '0pt',
    width: '100%',
  },
  tableHeader: {
    borderBottom: '1px solid #EAECF0',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '8pt 12pt',
  },
  tableData: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: '12pt',
  },
});

type PdfDocumentType = {
  graphReadsImg?: string;
  graphAlertsImg?: string;
  includeGraphs?: boolean;
  mapReadsImg?: string;
  mapAlertsImg?: string;
  includeMaps?: boolean;
  includeTable?: boolean;
  dateFrom: string;
  dateTo: string;
  alertsStats: Array<Record<string, string | number>>;
  readsStats: Array<Record<string, string | number>>;
  readsData: AggregatedReadsData;
  alertsData: AggregatedAlertsData;
  readsMode: 'site' | 'camera';
  alertsMode: 'alert' | 'site' | 'camera';
};
const PdfDocument = (props: PdfDocumentType) => {
  const {
    graphReadsImg, graphAlertsImg, includeGraphs, alertsData,
    mapReadsImg, mapAlertsImg, includeMaps, includeTable,
    dateFrom, dateTo, readsStats, alertsStats, readsData, readsMode, alertsMode,
  } = props;

  const rs = readsStats.find((stat) => stat.id === 'camera');
  const als = alertsStats.find((stat) => stat.id === 'camera');
  const getAlertData = () => {
    switch (alertsMode) {
      case 'alert':
        return alertsData.alertsData;
      case 'site':
        return alertsData.sitesData;
      default:
        return alertsData.camerasData;
    }
  };
  const readsDataToRender = readsMode === 'site' ? readsData.sitesData : readsData.camerasData;
  const alertDataToRender = getAlertData();
  const readsLength = readsDataToRender[0] ? Object.keys(readsDataToRender[0]).length : 0;
  const alertsLength = alertDataToRender[0] ? Object.keys(alertDataToRender[0]).length : 0;

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.headerWrapper}>
          <View style={styles.header}>
            <View>
              <Text style={styles.detailedReportText}> Detailed Report</Text>
              <View>
                <Text style={styles.rangeText}>
                  {dateFrom} - {dateTo}
                </Text>
              </View>
            </View>
            <Image style={styles.logo} src={logoPdf} />
          </View>
        </View>
        {rs && (
          <View style={styles.totalWrapper}>
            <View style={styles.totalReads}>
              <Text style={styles.totalReadsText}>
              Total Reads: {' '}
              </Text>
              <Text style={styles.totalReadsNumber}>
                {rs?.total}
              </Text>
            </View>
            <View style={styles.eachKeySeparateWrapper}>
              {Object.keys(rs || {}).filter((key) => key !== 'total' && key !== 'id').map((key) => (
                <View key={key} style={styles.eachKeySeparate}>
                  <Text style={styles.totalReadsText}>
                    {key}
                  </Text>
                  <Text style={styles.totalReadsNumber}>
                    {(rs || {})[key]}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        )}
        {als && (

          <View style={styles.totalWrapper}>
            <View style={styles.totalReads}>
              <Text style={styles.totalReadsText}>
              Total Alerts: {' '}
              </Text>
              <Text style={styles.totalReadsNumber}>
                {als?.total}
              </Text>
            </View>
            <View style={styles.eachKeySeparateWrapper}>
              {Object.keys(als || {}).filter((key) => key !== 'total' && key !== 'id').map((key) => (
                <View key={key} style={styles.eachKeySeparate}>
                  <Text style={styles.totalReadsText}>
                    {key}
                  </Text>
                  <Text style={styles.totalReadsNumber}>
                    {(als || {})[key]}
                  </Text>
                </View>
              ))}
            </View>
          </View>
        )}
        {includeGraphs && (
          <>
            <View>
              <Text style={styles.headerText}>
                Line Graph
              </Text>
            </View>
            <View style={styles.graph}>
              <Image style={{ width: '100%' }} src={graphReadsImg} />
            </View>
            <View style={styles.graph}>
              <Image style={{ width: '100%' }} src={graphAlertsImg} />
            </View>
          </>
        )}
        {includeTable && rs && (
          <>
            <View>
              <Text style={styles.headerText}>
                Reads over time
              </Text>
            </View>
            <View style={styles.tableWrapper}>
              <View style={styles.tableRow}>
                <View style={[styles.tableHeader, { width: `${100 / readsLength}%` }]}>
                  <Text style={styles.totalReadsText}>
                    Date/Time
                  </Text>
                </View>
                {Object.keys(readsDataToRender[0]).filter((key) => key !== 'correctDate').map((key) => (
                  <View key={key} style={[styles.tableHeader, { width: `${100 / readsLength}%` }]}>
                    <Text style={styles.totalReadsText}>
                      {key}
                    </Text>
                  </View>
                ))
                }
              </View>
              {
                readsDataToRender.slice(0, 8).map((data, index) => (
                  <View key={data.correctDate} style={[styles.tableRow, { backgroundColor: index % 2 ? 'white' : '#F9FAFB' }]}>
                    <View style={[styles.tableData, { width: `${100 / readsLength}%` }]}>
                      <Text style={styles.totalReadsNumber}>
                        {data.correctDate}
                      </Text>
                    </View>
                    {Object.keys(data).filter((key) => key !== 'correctDate').map((key) => (
                      <View key={key} style={[styles.tableData, { width: `${100 / readsLength}%` }]}>
                        <Text style={styles.totalReadsText}>
                          {data[key]}
                        </Text>
                      </View>
                    ))}
                  </View>
                ))
              }
            </View>
            {includeTable && als && (
              <>
                <View>
                  <Text style={styles.headerText}>
                Alerts over time
                  </Text>
                </View>
                <View style={styles.tableWrapper}>
                  <View style={styles.tableRow}>
                    <View style={[styles.tableHeader, { width: `${100 / alertsLength}%` }]}>
                      <Text style={styles.totalReadsText}>
                        Date/Time
                      </Text>
                    </View>
                    {
                      Object.keys(alertDataToRender[0]).filter((key) => key !== 'correctDate').map((key) => (
                        <View key={key} style={[styles.tableHeader, { width: `${100 / alertsLength}%` }]}>
                          <Text key={key} style={styles.totalReadsText}>
                            {key === 'correctDate' ? 'Date/Time' : key}
                          </Text>
                        </View>
                      ))
                    }
                  </View>
                  {
                    alertDataToRender.slice(0, 8).map((data, index) => (
                      <View key={data.correctDate} style={[styles.tableRow, { backgroundColor: index % 2 ? 'white' : '#F9FAFB' }]}>
                        <View style={[styles.tableData, { width: `${100 / alertsLength}%` }]}>
                          <Text style={styles.totalReadsNumber}>
                            {data.correctDate}
                          </Text>
                        </View>
                        {Object.keys(data).filter((key) => key !== 'correctDate').map((key) => (
                          <View key={key} style={[styles.tableData, { width: `${100 / alertsLength}%` }]}>
                            <Text key={key} style={key === 'correctDate' ? styles.totalReadsNumber : styles.totalReadsText}>
                              {data[key]}
                            </Text>
                          </View>
                        ))}
                      </View>
                    ))
                  }
                </View>
              </>
            )}
          </>
        )}
        {includeMaps && (
          <>
            <View>
              <Text style={styles.headerText}>
                Reads over time
              </Text>
            </View>
            <View style={styles.graph}>
              <Image style={{ width: '100%' }} src={mapReadsImg} />
            </View>
            <View>
              <View>
                <Text style={styles.headerText}>
                Alerts over time
                </Text>
              </View>
              <View style={styles.graph}>
                <Image style={{ width: '100%' }} src={mapAlertsImg} />
              </View>
            </View>
          </>
        )}
      </Page>
    </Document>
  );
};

export default PdfDocument;
